export const seoLandingPages = {
  weekByWeek: {
    slug: 'pregnancy-week-by-week',
    title: 'Pregnancy Week by Week | Zemuu',
    headline: 'Pregnancy Week by Week',
    eyebrow: 'Zemuu Guide',
    description:
      'Track pregnancy week by week with gentle guidance, trimester milestones, and supportive check-ins from Zemuu.',
    intro:
      'Every week brings a new shift—your baby grows, your body adapts, and your needs change. This guide helps you stay grounded with trimester highlights and a clear week-by-week view.',
    ctaText: 'Track your pregnancy with Zemuu',
    ctaLink: '/register',
    sections: [
      {
        title: 'Trimester overview',
        body:
          'Use this quick view to know what each trimester focuses on—foundation, growth, and preparation.',
        items: [
          { title: 'Trimester 1 (Weeks 1–13)', text: 'Foundation, energy shifts, early development, gentle support.' },
          { title: 'Trimester 2 (Weeks 14–27)', text: 'Growth, movement, steady energy, deeper connection.' },
          { title: 'Trimester 3 (Weeks 28–40)', text: 'Preparation, nesting, body support, appointment rhythm.' },
        ],
      },
      {
        title: 'Week-by-week links',
        body:
          'Jump to any week to create your own rhythm of learning and tracking.',
      },
    ],
    weekLinks: Array.from({ length: 40 }, (_, i) => {
      const week = i + 1;
      return {
        label: `Week ${week}`,
        href: `#week-${week}`,
      };
    }),
    weekHighlights: [
      { id: 'week-1', title: 'Week 1', subtitle: 'Trimester 1', text: 'Your body is preparing. Focus on hydration and consistent rest.' },
      { id: 'week-2', title: 'Week 2', subtitle: 'Trimester 1', text: 'Foundation week. Prioritize folate and gentle movement.' },
      { id: 'week-3', title: 'Week 3', subtitle: 'Trimester 1', text: 'Early changes start. Keep meals simple and nutrient-dense.' },
      { id: 'week-4', title: 'Week 4', subtitle: 'Trimester 1', text: 'Energy may dip. Aim for protein with each meal.' },
      { id: 'week-5', title: 'Week 5', subtitle: 'Trimester 1', text: 'Nausea can begin. Choose warm, easy foods and rest.' },
      { id: 'week-6', title: 'Week 6', subtitle: 'Trimester 1', text: 'Hormones rise. Light walks and steady hydration help.' },
      { id: 'week-7', title: 'Week 7', subtitle: 'Trimester 1', text: 'Sleep may shift. Build a calming bedtime routine.' },
      { id: 'week-8', title: 'Week 8', subtitle: 'Trimester 1', text: 'Stomach sensitivity is common. Small, frequent meals support you.' },
      { id: 'week-9', title: 'Week 9', subtitle: 'Trimester 1', text: 'Energy fluctuates. Pair carbs with protein to stabilize.' },
      { id: 'week-10', title: 'Week 10', subtitle: 'Trimester 1', text: 'Your baby is growing fast. Stay consistent with folate and iron.' },
      { id: 'week-11', title: 'Week 11', subtitle: 'Trimester 1', text: 'Gentle movement helps circulation and mood.' },
      { id: 'week-12', title: 'Week 12', subtitle: 'Trimester 1', text: 'Transition week. Notice what foods feel best right now.' },
      { id: 'week-13', title: 'Week 13', subtitle: 'Trimester 1', text: 'Wrap up trimester one with rest and hydration focus.' },
      { id: 'week-14', title: 'Week 14', subtitle: 'Trimester 2', text: 'Energy often lifts. Add more protein and calcium.' },
      { id: 'week-15', title: 'Week 15', subtitle: 'Trimester 2', text: 'Build steady routines: meals, movement, and sleep.' },
      { id: 'week-16', title: 'Week 16', subtitle: 'Trimester 2', text: 'Your baby’s growth accelerates. Iron-rich foods help.' },
      { id: 'week-17', title: 'Week 17', subtitle: 'Trimester 2', text: 'Hydration supports circulation. Aim for steady intake.' },
      { id: 'week-18', title: 'Week 18', subtitle: 'Trimester 2', text: 'Gentle strength or walking builds stamina.' },
      { id: 'week-19', title: 'Week 19', subtitle: 'Trimester 2', text: 'Nourish with colorful vegetables and omega‑3s.' },
      { id: 'week-20', title: 'Week 20', subtitle: 'Trimester 2', text: 'Halfway point. Check in with rest, posture, and breath.' },
      { id: 'week-21', title: 'Week 21', subtitle: 'Trimester 2', text: 'Add fiber and water to support digestion.' },
      { id: 'week-22', title: 'Week 22', subtitle: 'Trimester 2', text: 'Movement helps circulation and swelling.' },
      { id: 'week-23', title: 'Week 23', subtitle: 'Trimester 2', text: 'Consistent meals keep energy steady.' },
      { id: 'week-24', title: 'Week 24', subtitle: 'Trimester 2', text: 'Focus on iron + vitamin C pairings.' },
      { id: 'week-25', title: 'Week 25', subtitle: 'Trimester 2', text: 'Short breaks and stretching support comfort.' },
      { id: 'week-26', title: 'Week 26', subtitle: 'Trimester 2', text: 'Hydration and magnesium can ease muscle cramps.' },
      { id: 'week-27', title: 'Week 27', subtitle: 'Trimester 2', text: 'Prepare for trimester three with more rest.' },
      { id: 'week-28', title: 'Week 28', subtitle: 'Trimester 3', text: 'Visits increase. Keep snacks protein‑rich.' },
      { id: 'week-29', title: 'Week 29', subtitle: 'Trimester 3', text: 'Sleep positioning matters. Support hips and back.' },
      { id: 'week-30', title: 'Week 30', subtitle: 'Trimester 3', text: 'Focus on iron, hydration, and smaller meals.' },
      { id: 'week-31', title: 'Week 31', subtitle: 'Trimester 3', text: 'Gentle movement and stretch breaks help comfort.' },
      { id: 'week-32', title: 'Week 32', subtitle: 'Trimester 3', text: 'Breathwork can reduce tension and anxiety.' },
      { id: 'week-33', title: 'Week 33', subtitle: 'Trimester 3', text: 'Keep a steady meal rhythm to reduce heartburn.' },
      { id: 'week-34', title: 'Week 34', subtitle: 'Trimester 3', text: 'Prepare questions for upcoming appointments.' },
      { id: 'week-35', title: 'Week 35', subtitle: 'Trimester 3', text: 'Focus on hydration and rest between tasks.' },
      { id: 'week-36', title: 'Week 36', subtitle: 'Trimester 3', text: 'Light activity supports circulation and mood.' },
      { id: 'week-37', title: 'Week 37', subtitle: 'Trimester 3', text: 'Finalize your comfort plan and support rituals.' },
      { id: 'week-38', title: 'Week 38', subtitle: 'Trimester 3', text: 'Stay nourished with easy, familiar meals.' },
      { id: 'week-39', title: 'Week 39', subtitle: 'Trimester 3', text: 'Keep hydration steady and rest whenever possible.' },
      { id: 'week-40', title: 'Week 40', subtitle: 'Trimester 3', text: 'You’re close. Gentle walks and calm routines help.' },
    ],
    faq: [
      {
        question: 'How do I track my pregnancy week by week?',
        answer:
          'Use Zemuu to log your week, review trimester milestones, and follow gentle weekly guidance tailored to your stage.',
      },
      {
        question: 'When does each trimester start?',
        answer:
          'Trimester 1 is weeks 1–13, Trimester 2 is weeks 14–27, and Trimester 3 is weeks 28–40.',
      },
      {
        question: 'Can I switch between weeks?',
        answer:
          'Yes—this page lets you jump to any week so you can review what matters most right now.',
      },
      {
        question: 'Is week 1 the same as conception?',
        answer:
          'Week 1 begins with the start of your last menstrual period. Conception usually happens around week 2–3.',
      },
      {
        question: 'What if I do not know my exact week?',
        answer:
          'Use your LMP or estimated due date in Zemuu and it will calculate your current week for you.',
      },
    ],
  },
  nutritionByWeek: {
    slug: 'pregnancy-nutrition-by-week',
    title: 'Pregnancy Nutrition by Week | Zemuu',
    headline: 'Pregnancy Nutrition by Week',
    eyebrow: 'Zemuu Nutrition',
    description:
      'Learn pregnancy nutrition by week with trimester-focused guidance, nutrient priorities, and meal support from Zemuu.',
    intro:
      'Nutrition needs shift as your baby grows. This guide highlights what to focus on each trimester and how to build meals that feel supportive—without perfection pressure.',
    ctaText: 'Build your pregnancy meal plan',
    ctaLink: '/register',
    sections: [
      {
        title: 'Trimester focus',
        body:
          'Think in rhythms: stabilizing early, fueling growth mid, and preparing late.',
        items: [
          { title: 'Trimester 1', text: 'Gentle foods, hydration, folate, B6, and iron support.' },
          { title: 'Trimester 2', text: 'Protein, calcium, omega‑3s, and steady energy.' },
          { title: 'Trimester 3', text: 'Iron, magnesium, hydration, and meal timing to reduce discomfort.' },
        ],
      },
      {
        title: 'Weekly nourishment rhythm',
        body:
          'Use this as a weekly check‑in for what to emphasize in meals and snacks.',
        items: [
          { title: 'Weeks 1–8', text: 'Hydration, easy‑to‑tolerate foods, and folate‑rich meals.' },
          { title: 'Weeks 9–13', text: 'Protein anchors, fiber, and gentle appetite support.' },
          { title: 'Weeks 14–20', text: 'Calcium + omega‑3s, colorful veggies, consistent energy.' },
          { title: 'Weeks 21–27', text: 'Iron + vitamin C pairings, balanced meals.' },
          { title: 'Weeks 28–34', text: 'Magnesium, hydration, smaller frequent meals.' },
          { title: 'Weeks 35–40', text: 'Comfort foods, hydration, and steady protein.' },
        ],
      },
    ],
    faq: [
      {
        question: 'Do nutrition needs change every week?',
        answer:
          'The biggest shifts happen by trimester, but weekly check‑ins help you stay consistent and respond to appetite changes.',
      },
      {
        question: 'What nutrients matter most during pregnancy?',
        answer:
          'Folate, iron, calcium, protein, omega‑3s, and hydration are key across all trimesters.',
      },
      {
        question: 'Is a meal planner helpful during pregnancy?',
        answer:
          'Yes—simple planning reduces stress and helps you hit nutrient targets without overthinking.',
      },
      {
        question: 'What should I eat when nausea is strong?',
        answer:
          'Focus on bland, easy foods, small portions, and hydration. Ginger, crackers, and warm soups are common supports.',
      },
      {
        question: 'How much protein is recommended?',
        answer:
          'Most people need about 60–75g daily, depending on trimester and body needs.',
      },
    ],
  },
  appointmentsGuide: {
    slug: 'pregnancy-appointments-guide',
    title: 'Pregnancy Appointment Guide | Zemuu',
    headline: 'Pregnancy Appointments, Explained',
    eyebrow: 'Zemuu Care',
    description:
      'Understand pregnancy appointments by trimester, why they matter, and how to prepare—so you feel confident and informed.',
    intro:
      'Appointments are checkpoints that keep you and your baby supported. This guide helps you understand the timing, purpose, and how to prepare for each stage.',
    ctaText: 'Plan your appointments with Zemuu',
    ctaLink: '/register',
    sections: [
      {
        title: 'Why appointments matter',
        body:
          'Visits aren’t just routine—they help confirm growth, catch concerns early, and answer your questions.',
        items: [
          { title: 'Safety', text: 'Monitor growth, vitals, and early signs of concern.' },
          { title: 'Clarity', text: 'Get answers on symptoms, nutrition, and mental wellbeing.' },
          { title: 'Preparation', text: 'Plan ahead for scans, tests, and birth decisions.' },
        ],
      },
      {
        title: 'Trimester timeline',
        body:
          'Use this outline to understand what’s typical and what each visit often includes.',
        items: [
          { title: 'First trimester', text: 'Confirmation visit, dating scan, early labs and risk screening.' },
          { title: 'Second trimester', text: 'Anatomy scan, growth monitoring, steady check‑ins.' },
          { title: 'Third trimester', text: 'More frequent visits, position checks, birth planning.' },
        ],
      },
    ],
    faq: [
      {
        question: 'How often are pregnancy appointments?',
        answer:
          'Most schedules are every 4 weeks early on, then every 2 weeks, and weekly near the end—your provider will customize this.',
      },
      {
        question: 'What should I ask at appointments?',
        answer:
          'Ask about symptoms, nutrition, movement, test results, and what to expect before the next visit.',
      },
      {
        question: 'Can Zemuu remind me about visits?',
        answer:
          'Yes—Zemuu helps you plan and track appointments so nothing falls through the cracks.',
      },
      {
        question: 'What should I bring to appointments?',
        answer:
          'Bring your questions, medication list, and any symptom notes to help your provider support you.',
      },
      {
        question: 'What if I miss an appointment?',
        answer:
          'Reschedule as soon as possible and let your care team know about any concerning symptoms.',
      },
    ],
  },
};

